import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Injects Google Analytics (GA4) and/or Google Tag Manager tags on the public site
 * when the current project has them configured. Also sends SPA page_view events on
 * route changes so GA Realtime shows accurate navigation.
 *
 * The tags load once per full-page load; subsequent SPA navigations dispatch a
 * `page_view` gtag event. Real-time verification works in the standard GA UI
 * and Google Tag Assistant.
 */
const GoogleAnalytics = ({ gaId, gtmId }) => {
  const location = useLocation();

  // Inject GA4 gtag.js once
  useEffect(() => {
    if (!gaId || typeof window === "undefined") return;
    if (window.__gaLoadedId === gaId) return;

    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    script1.setAttribute("data-ga-id", gaId);
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.setAttribute("data-ga-inline", gaId);
    script2.text = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);} window.gtag = gtag;
gtag('js', new Date());
gtag('config', '${gaId}', { send_page_view: true });`;
    document.head.appendChild(script2);

    window.__gaLoadedId = gaId;
  }, [gaId]);

  // Inject GTM once
  useEffect(() => {
    if (!gtmId || typeof window === "undefined") return;
    if (window.__gtmLoadedId === gtmId) return;

    const script = document.createElement("script");
    script.setAttribute("data-gtm-id", gtmId);
    script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(script);

    // <noscript> iframe fallback
    const noscript = document.createElement("noscript");
    noscript.setAttribute("data-gtm-noscript", gtmId);
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    window.__gtmLoadedId = gtmId;
  }, [gtmId]);

  // Send SPA page_view on route change
  useEffect(() => {
    if (!gaId || typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [gaId, location.pathname, location.search]);

  return null;
};

export default GoogleAnalytics;
